import { put, takeLatest } from 'redux-saga/effects';
import { AccountConfig } from 'api/account';
import { FieldSectionType } from 'api/field-sections/types';
import { Field, Task, TaskAPI } from 'api/tasks';
import { StoreActionProps } from 'data/core';
import { STATUS_FIELD_NAME } from 'data/fields/hooks/account-fields';
import { ACCOUNT_CONFIG_QUERY_KEY } from 'queries/account';
import {
  getProjectDependencyMapKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import { logger } from 'services';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';
import {
  CreateSubTaskActionPayload,
  createSubTaskSagaAction,
} from '../actions';
import { addSubTaskToTask, createSubTaskSuccess } from '../slice';

function* createSubTaskSaga(
  action: StoreActionProps<CreateSubTaskActionPayload>,
): Generator {
  const { payload } = action;

  const { projectId, taskId, queryClient, updateLocalState, ...rest } = payload;

  const accountConfig = queryClient.getQueryData<AccountConfig>(
    ACCOUNT_CONFIG_QUERY_KEY,
  );
  const statusField = accountConfig?.fields?.find(
    (f) =>
      f.fieldName === STATUS_FIELD_NAME &&
      f.objectType === FieldSectionType.Task,
  ) as Field | undefined;

  if (!statusField) {
    return;
  }

  const { fieldId } = statusField;
  const fieldValue = statusField.metaData?.default as number;

  try {
    yield put(
      addSubTaskToTask({
        projectId,
        taskId,
        parentTask: rest.parentTask,
        taskName: rest.taskName,
        fields: [{ fieldId, fieldValue }],
      }),
    );
    const task = (yield TaskAPI.createTask(projectId, rest)) as Task;

    // This is done to optimistically update the local state for sub tasks list.
    if (updateLocalState) {
      updateLocalState(task);
    }

    yield put(
      createSubTaskSuccess({
        projectId,
        parentTask: rest.parentTask,
        taskId,
        task,
      }),
    );
    yield invalidateTaskQuery(taskId);
    yield invalidateTaskQuery(rest.parentTask);
    yield queryClient.refetchQueries(
      getProjectTasksQueryKey(['project_id', projectId]),
    );
    yield queryClient.refetchQueries(getProjectDependencyMapKey(projectId));
  } catch (error) {
    logger.error('createSubTaskSaga failed', { error });
  }
}

export function* watchCreateSubTaskSaga(): Generator {
  yield takeLatest(createSubTaskSagaAction.type, createSubTaskSaga);
}
